/**
 * AddFieldButton - "+" affordance with two visual variants.
 *
 * Variants:
 * - `'inline-handle'` - small "+" intended to sit OUTSIDE a `FieldCard`'s left
 *   edge, on the absolutely-positioned hover rail next to the drag grip. Used
 *   per-block, hover-revealed by the parent.
 * - `'bottom'` - large circular accent button rendered once at the bottom of
 *   the canvas to append a new field to the end of the conversation. Always visible.
 *
 * Both variants open the same five-family `FieldTypeMenu` popover.
 *
 * @module components/form-builder/AddFieldButton
 */

import React, { useCallback, useRef, useState } from 'react';
import type { FieldType } from '../../types';
import type { FormFieldFormat } from '../../lib/form-capabilities';
import type { NestedFormSelection } from './nested-form-sources';
import { FieldTypeMenu } from './FieldTypeMenu';

/** Props accepted by `AddFieldButton`. */
export interface AddFieldButtonProps {
  /** Index in the FormField[] where the new field should be inserted. */
  insertIndex: number;
  /**
   * Called when an enabled field type is selected. Receives the insertion
   * index and the chosen field type so the wizard can build the right factory
   * (free-form vs. information block). The type is optional for legacy callers
   * that only ever produced free-form fields. The wizard handles ID generation
   * and field stamping (`mustOrTry: 'try'`).
   */
  onAddField: (insertIndex: number, type?: FieldType, format?: FormFieldFormat) => void;
  /** Start a new section at this insertion point when the host supports it. */
  onAddSection?: (insertIndex: number) => void;
  /** Embed another workspace form at this position. */
  onAddNestedForm?: (insertIndex: number) => NestedFormSelection;
  /** Generate one question at the selected insertion point. */
  onGenerateField?: (insertIndex: number, description: string, signal: AbortSignal) => Promise<void>;
  /**
   * Visual variant. `'inline-handle'` is small and meant for the per-card left
   * rail; `'bottom'` is a large round accent button for the end-of-canvas
   * append affordance. Defaults to `'inline-handle'` (per-card rail).
   */
  variant?: 'inline-handle' | 'bottom';
  /**
   * When true, a calendar block already exists in the schema, so the menu hides
   * its "Calendar" option (milestone-1 one-per-campaign rule).
   */
  hasCalendarBlock?: boolean;
  /** Optional className for the wrapper. */
  className?: string;
}

/**
 * Plus-glyph icon used inside both variants. Inline so we don't drag in a new
 * icon dependency just for the rail.
 */
const PlusGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    aria-hidden="true"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

/**
 * Click "+" to open the definition's `FieldTypeMenu`.
 */
export const AddFieldButton: React.FC<AddFieldButtonProps> = ({
  insertIndex,
  onAddField,
  onAddSection,
  onAddNestedForm,
  onGenerateField,
  variant = 'inline-handle',
  hasCalendarBlock = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const handleSelect = useCallback(
    (type: FieldType, format?: FormFieldFormat) => {
      // Forward the selected type so the wizard builds the matching factory
      // (free-form vs. information block). Then close the menu.
      if (format) onAddField(insertIndex, type, format);
      else onAddField(insertIndex, type);
      setIsOpen(false);
    },
    [insertIndex, onAddField],
  );

  const isBottom = variant === 'bottom';

  return (
    <div className={`relative ${isBottom ? 'flex justify-center' : ''} ${className ?? ''}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Add field"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
        className={
          isBottom
            ? 'group/add flex h-9 w-9 items-center justify-center rounded-full bg-volt/20 text-volt-soft hover:bg-volt hover:text-accent-foreground transition-colors cursor-pointer'
            : 'group/add flex h-6 w-6 items-center justify-center rounded-lg text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors cursor-pointer'
        }
      >
        <PlusGlyph className={isBottom ? 'h-4 w-4' : 'h-4 w-4'} />
      </button>

      <FieldTypeMenu
        isOpen={isOpen}
        onSelect={handleSelect}
        onAddSection={onAddSection ? () => { onAddSection(insertIndex); setIsOpen(false); } : undefined}
        onAddNestedForm={onAddNestedForm ? () => onAddNestedForm(insertIndex) : undefined}
        hasCalendarBlock={hasCalendarBlock}
        onClose={() => setIsOpen(false)}
        outsideClickIgnoreRef={triggerRef}
        anchorRef={triggerRef}
        onGenerate={onGenerateField ? (description, signal) => onGenerateField(insertIndex, description, signal) : undefined}
      />
    </div>
  );
};
